import { Rocket, Receipt, CreditCard, ShoppingCart, Tag, ArrowRight } from 'lucide-react';
import { useModules, useToggleModule } from '@/hooks/use-modules';
import { useLocationStore } from '@/stores/location-store';
import { cn } from '@/lib/utils';

const MODULE_INFO: Record<string, { name: string; description: string; icon: React.ElementType }> = {
  collections: {
    name: 'Receive Payments',
    description: 'Manage invoices and collect payments from customers',
    icon: Receipt,
  },
  payables: {
    name: 'Send Payments',
    description: 'Manage vendor payments and bills',
    icon: CreditCard,
  },
  products: {
    name: 'POS Management',
    description: 'Manage point-of-sale products, departments, and inventory',
    icon: ShoppingCart,
  },
  esl: {
    name: 'Shelf Labels',
    description: 'Digital price labels on your shelves, updated automatically',
    icon: Tag,
  },
};

export function OnboardingPage() {
  const { selectedLocationId } = useLocationStore();
  const { data: modulesData, isLoading } = useModules(selectedLocationId);
  const toggleModule = useToggleModule();
  const modules = modulesData?.data ?? [];
  const activeCount = modules.filter((m) => m.isActive).length;
  const offered = Object.entries(MODULE_INFO).filter(([key]) => modules.some((m) => m.moduleName === key));

  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <div className="w-full max-w-2xl">
        {/* Welcome header */}
        <div className="mb-8 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
            <Rocket className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">Welcome to Fintech - Q PaymentZ!</h1>
          <p className="mt-2 text-muted-foreground">
            Get started by selecting the modules you need for your business. Once you've made your selections, our team
            will review and activate your account.
          </p>
        </div>

        {/* Module selection */}
        <div className="mb-6">
          <h2 className="mb-4 text-center text-lg font-semibold text-foreground">Choose Your Modules</h2>
          {isLoading ? (
            <div className="flex h-32 items-center justify-center text-muted-foreground">Loading modules...</div>
          ) : (
            <div
              className={cn(
                'grid gap-4',
                // A shelf-labels-only business is offered exactly one module;
                // a lone card stranded in a two-column grid reads as a page
                // that failed to load the rest.
                offered.length > 1 ? 'sm:grid-cols-2' : 'mx-auto max-w-sm',
              )}
            >
              {/* Intersect the presentable modules with the ones the API
                  actually offers this business. MODULE_INFO alone is a
                  hardcoded list that no filtering reaches, so a shelf-labels
                  business was shown three modules it can never use and not the
                  one it signed up for. The API already narrows by account kind
                  and by the platform release switch; this just renders what it
                  says. */}
              {offered.map(([key, info]) => {
                const mod = modules.find((m) => m.moduleName === key);
                const isActive = mod?.isActive || false;
                const granted = mod?.superAdminEnabled ?? false;
                // The module has been live at some point if activatedAt is set.
                // If the super admin later closes the gate, this is a revoke
                // (hard lock — business can't resurrect on its own). If
                // activatedAt is null, the business is in registration mode
                // and may freely express interest by toggling this tile.
                const revoked = !granted && !!mod?.activatedAt;
                // Visual states:
                //   live      — granted + isActive
                //   pending   — !granted + isActive (request awaiting admin)
                //   available — granted + !isActive
                //   revoked   — !granted + activatedAt set (hard lock)
                //   default   — !granted + !isActive (click to express interest)
                const live = granted && isActive;
                const pending = !granted && isActive && !revoked;
                const Icon = info.icon;

                return (
                  <button
                    key={key}
                    onClick={() => {
                      if (revoked) return;
                      toggleModule.mutate({ moduleName: key, isActive: !isActive, locationId: selectedLocationId });
                    }}
                    disabled={toggleModule.isPending || revoked}
                    title={
                      revoked
                        ? 'This module was disabled by your administrator and cannot be changed. Contact support to unlock it.'
                        : !granted
                          ? 'Tap to request this module. Your administrator will review and grant access.'
                          : undefined
                    }
                    className={cn(
                      'flex flex-col items-center gap-3 rounded-xl border-2 p-6 text-center transition-all',
                      revoked
                        ? 'cursor-not-allowed border-border bg-muted/30 opacity-60'
                        : live
                          ? 'border-primary bg-primary/5 shadow-sm'
                          : pending
                            ? 'border-amber-300 bg-amber-50 shadow-sm'
                            : isActive
                              ? 'border-primary bg-primary/5 shadow-sm'
                              : 'border-border bg-white hover:border-primary/40 hover:bg-primary/5',
                    )}
                  >
                    <div
                      className={cn(
                        'flex h-12 w-12 items-center justify-center rounded-lg',
                        revoked
                          ? 'bg-muted text-muted-foreground'
                          : pending
                            ? 'bg-amber-100 text-amber-800'
                            : isActive
                              ? 'bg-primary text-white'
                              : 'bg-muted text-muted-foreground',
                      )}
                    >
                      <Icon className="h-6 w-6" />
                    </div>
                    <h3 className="font-medium text-foreground">{info.name}</h3>
                    <p className="text-xs text-muted-foreground">{info.description}</p>
                    <span
                      className={cn(
                        'mt-1 rounded-full px-3 py-1 text-xs font-medium',
                        revoked
                          ? 'bg-amber-100 text-amber-800'
                          : pending
                            ? 'bg-amber-100 text-amber-800'
                            : live
                              ? 'bg-primary/10 text-primary'
                              : isActive
                                ? 'bg-primary/10 text-primary'
                                : 'bg-muted text-muted-foreground',
                      )}
                    >
                      {revoked
                        ? 'Locked by admin'
                        : pending
                          ? 'Pending approval'
                          : live
                            ? 'Active'
                            : isActive
                              ? 'Selected'
                              : 'Click to Select'}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Status hint & Continue button */}
        <div className="rounded-lg border border-border bg-white p-4 text-center">
          {activeCount > 0 ? (
            <>
              <p className="text-sm text-muted-foreground">
                <span className="font-medium text-primary">{activeCount}</span> module{activeCount > 1 ? 's' : ''}{' '}
                selected. Continue to complete your business registration.
              </p>
              <button
                onClick={() =>
                  (window.location.hash = selectedLocationId ? `#/register/${selectedLocationId}` : '#/register')
                }
                className="mt-3 inline-flex h-10 items-center gap-2 rounded-lg bg-primary px-6 text-sm font-medium text-primary-foreground hover:bg-primary/90"
              >
                Continue Registration
                <ArrowRight className="h-4 w-4" />
              </button>
            </>
          ) : (
            <p className="text-sm text-muted-foreground">
              Select at least one module to get started with your business setup.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
